import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {FaqSlice} from 'prismicio-types'
import type {Dispatch, SetStateAction} from 'react'
import {memo, useCallback, useMemo} from 'react'
import {ButtonV2} from 'src/common/ButtonV2'
import {Icon} from 'src/common/Icon'

export const Item = memo(function (props: {
  activeItem: number | null
  answer: FaqSlice['items'][number]['answer']
  className?: string
  index: number
  question: FaqSlice['items'][number]['question']
  setActiveItem: Dispatch<SetStateAction<number | null>>
}) {
  const isOpen = useMemo(() => props.activeItem === props.index, [props.activeItem, props.index])

  const toggleActiveItem = useCallback(
    () => props.setActiveItem((prev) => (prev === props.index ? null : props.index)),
    [props],
  )

  return (
    <button
      className={cn(
        'grid overflow-hidden border-b border-grey-150 pb-4 text-left',
        {
          'grid-rows-[auto_0fr]': !isOpen,
          'grid-rows-[auto_1fr]': isOpen,
        },
        props.className,
      )}
      onClick={toggleActiveItem}
    >
      <h3 className="grid cursor-pointer grid-flow-col items-center justify-between gap-x-4">
        <div className="text-h5 leading-7 text-moss-black">{props.question}</div>
        <Icon className="size-6 text-moss-black" name={isOpen ? 'minus' : 'plus'} />
      </h3>

      <div
        className={cn('grid min-h-0 gap-y-4 text-16 leading-6 text-666563', {'opacity-0': !isOpen}, {'mt-4': isOpen})}
      >
        <PrismicRichText
          components={{
            list: ({children}) => <ul className="grid list-disc gap-y-2 pl-10">{children}</ul>,
            listItem: ({children}) => <li className="pl-2">{children}</li>,
            oList: ({children}) => <ul className="grid list-decimal gap-y-2 pl-10">{children}</ul>,
            oListItem: ({children}) => <li className="pl-2">{children}</li>,
          }}
          field={props.answer}
        />
      </div>
    </button>
  )
})

export const ItemV2 = memo(function (props: {
  activeItem: number | null
  answer: FaqSlice['items'][number]['answer']
  className?: string
  index: number
  question: FaqSlice['items'][number]['question']
  setActiveItem: Dispatch<SetStateAction<number | null>>
  pageUid: string
  chevronIconBackground?: string
}) {
  const isOpen = useMemo(() => props.activeItem === props.index, [props.activeItem, props.index])
  const {linkResolver} = useLinkResolver()

  const toggleActiveItem = useCallback(
    () => props.setActiveItem((prev) => (prev === props.index ? null : props.index)),
    [props],
  )

  return (
    <div
      className={cn(
        'box-border grid overflow-hidden py-4 text-left transition-all duration-300',
        {
          'grid-rows-[auto_0fr]': !isOpen,
          'grid-rows-[auto_1fr]': isOpen,
        },
        props.className,
      )}
      onClick={toggleActiveItem}
    >
      <h3 className="grid cursor-pointer grid-flow-col items-center justify-between gap-x-4">
        <div className="text-body-bold text-moss-black">{props.question}</div>
        <ChevronIcon
          isOpen={isOpen}
          index={props.index}
          pageUid={props.pageUid}
          backgroundColor={props.chevronIconBackground}
        />
      </h3>

      <div
        className={cn('text-body grid min-h-0 max-w-[600px] gap-y-4 text-grey-550 transition-all  duration-300', {
          'opacity-0': !isOpen,
        })}
      >
        <PrismicRichText
          components={{
            list: ({children}) => <ul className="mt-2 grid list-disc gap-y-2 pl-10">{children}</ul>,
            listItem: ({children}) => <li className="mt-2 pl-2">{children}</li>,
            oList: ({children}) => <ul className="mt-2 grid list-decimal gap-y-2 pl-10">{children}</ul>,
            oListItem: ({children}) => <li className="mt-2 pl-2">{children}</li>,
            paragraph: ({children}) => <p className="mt-2">{children}</p>,
            hyperlink: ({node, children}) => (
              <Link
                className="underline underline-offset-2 transition-colors duration-150"
                href={linkResolver(node.data)}
                {...(node.data.link_type === 'Web' && node.data.target && {target: node.data.target})}
                {...(node.data.link_type === 'Document' && node.data.lang && {locale: node.data.lang})}
              >
                {children}
              </Link>
            ),
          }}
          field={props.answer}
        />
      </div>
    </div>
  )
})

const ChevronIcon = ({
  isOpen,
  index,
  pageUid,
  backgroundColor,
}: {
  isOpen: boolean
  index: number
  pageUid: string
  backgroundColor?: string
}) => (
  <ButtonV2
    variant="icon-secondary"
    size="small"
    className={cn('relative max-h-8 min-h-8 min-w-8 !rounded-[3px] lg:p-0', {
      'lg:bg-transparent lg:hover:bg-transparent': !backgroundColor || backgroundColor === 'Transparent',
      'lg:bg-moss-white lg:hover:bg-moss-white': backgroundColor === 'Moss White',
    })}
    trackingData={{
      mktgId: 'faq-button',
      dataType: `order-${index}`,
      location: 'faq',
      pageName: pageUid,
    }}
    border
  >
    <Icon
      name="chevron-v2"
      className={cn('h-2.5 min-w-[10px] transition-transform duration-300 lg:text-black/[70%]', {
        'rotate-0': isOpen,
        'rotate-180': !isOpen,
      })}
    />
  </ButtonV2>
)
